import React, { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
  UncontrolledDropdown,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import { requestJson } from "../../../../helpers/api";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import esLocale from "@fullcalendar/core/locales/es";

type ProgresoEtapa = "idle" | "connecting" | "downloading" | "saving";

type PlataformasSincronizacionPanelProps = {
  titulo: string;
  descripcion: string;
  sincronizarEndpoint: string;
  verificarEndpoint?: string;
  empresaCodigoLabel?: string;
  empresaCodigoPlaceholder?: string;
  empresasEndpoint?: string;
};

type SincronizacionResponse = {
  ok?: boolean;
  conciliacionId?: number;
  totalRegistros?: number;
  diasProcesados?: string[];
  diasReemplazados?: string[];
  message?: string;
};

type VerificacionResponse = {
  ok?: boolean;
  diasConDatos?: string[];
  diasSinDatos?: string[];
  enProceso?: {
    fechaDesde: string;
    fechaHasta: string;
    usuarioNombre?: string | null;
    usuarioId?: number | null;
    empresaCodigo?: string | null;
  } | null;
};

type EmpresaOption = {
  empresaCodigo?: string | null;
  empresaNombre?: string | null;
  fuente?: string | null;
};

type EmpresasResponse = {
  ok?: boolean;
  empresas?: EmpresaOption[];
};

type ConciliacionHistorialEntry = {
  id: number;
  fechaDesde: string;
  fechaHasta: string;
  diasDetalle: Array<{ fecha: string; totalRegistros: number; totalMonto: number }>;
  totalRegistros?: number;
  usuarioNombre?: string | null;
  usuarioId?: number;
  empresaCodigo: string;
  empresaNombre?: string | null;
  creadoEn: string;
  duracionSegundos?: number | null;
};

type ConciliacionDetalleSucursal = {
  idsucursal: number | null;
  sucursalNombre: string;
  totalRegistros: number;
  totalMonto: number;
};

const buildIsoDate = (offsetDays: number) => {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  return date.toISOString().slice(0, 10);
};

const appendQueryParams = (endpoint: string, params: URLSearchParams) => {
  const connector = endpoint.includes("?") ? "&" : "?";
  return `${endpoint}${connector}${params.toString()}`;
};

const contarDiasSeleccionados = (desde: string, hasta: string) => {
  if (!desde || !hasta) {
    return 0;
  }
  const inicio = new Date(`${desde}T00:00:00`);
  const fin = new Date(`${hasta}T00:00:00`);
  if (Number.isNaN(inicio.getTime()) || Number.isNaN(fin.getTime())) {
    return 0;
  }
  if (fin < inicio) {
    return 0;
  }
  const diffMs = fin.getTime() - inicio.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;
};

const PlataformasSincronizacionPanel = ({
  titulo,
  descripcion,
  sincronizarEndpoint,
  verificarEndpoint,
  empresaCodigoLabel = "Empresa",
  empresaCodigoPlaceholder,
  empresasEndpoint = "/api/tesoreria/importaciones/monchis/mapeos-sucursales/opciones",
}: PlataformasSincronizacionPanelProps) => {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [fechaDesde, setFechaDesde] = useState(() => buildIsoDate(-8));
  const [fechaHasta, setFechaHasta] = useState(() => buildIsoDate(-1));
  const [rangoFechas, setRangoFechas] = useState<Date[]>([]);
  const [empresaCodigo, setEmpresaCodigo] = useState("");
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [ultimoResumen, setUltimoResumen] = useState<{
    diasProcesados?: string[];
    totalRegistros?: number;
    conciliacionId?: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [verificacion, setVerificacion] = useState<VerificacionResponse | null>(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [verificando, setVerificando] = useState(false);
  const [empresas, setEmpresas] = useState<EmpresaOption[]>([]);
  const [cargandoEmpresas, setCargandoEmpresas] = useState(false);
  const [errorEmpresas, setErrorEmpresas] = useState<string | null>(null);
  const [progressStage, setProgressStage] = useState<ProgresoEtapa>("idle");
  const progressTimeoutsRef = React.useRef<NodeJS.Timeout[]>([]);
  const [historial, setHistorial] = useState<ConciliacionHistorialEntry[]>([]);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  const [errorHistorial, setErrorHistorial] = useState<string | null>(null);
  const [historialTotal, setHistorialTotal] = useState(0);
  const [historialPage, setHistorialPage] = useState(1);
  const [historialLimit] = useState(10);
  const [detalleSucursales, setDetalleSucursales] = useState<ConciliacionDetalleSucursal[]>([]);
  const [detalleModalVisible, setDetalleModalVisible] = useState(false);
  const [detalleFecha, setDetalleFecha] = useState<string | null>(null);
  const [detalleConciliacionId, setDetalleConciliacionId] = useState<number | null>(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [detalleError, setDetalleError] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    const cargarEmpresas = async () => {
      if (!empresasEndpoint) {
        setEmpresas([]);
        return;
      }
      setCargandoEmpresas(true);
      setErrorEmpresas(null);
      try {
        const respuesta = await requestJson<EmpresasResponse>(empresasEndpoint);
        if (cancelado) return;
        setEmpresas(respuesta.empresas ?? []);
      } catch (err) {
        if (cancelado) return;
        const mensaje =
          err instanceof Error
            ? err.message
            : "No se pudieron cargar las empresas disponibles.";
        setErrorEmpresas(mensaje);
      } finally {
        if (!cancelado) {
          setCargandoEmpresas(false);
        }
      }
    };
    void cargarEmpresas();
    return () => {
      cancelado = true;
    };
  }, [empresasEndpoint]);

  useEffect(() => {
    if (!modalAbierto) return;
    const fechas: Date[] = [];
    if (fechaDesde) {
      const desde = new Date(`${fechaDesde}T00:00:00`);
      if (!Number.isNaN(desde.getTime())) {
        fechas.push(desde);
      }
    }
    if (fechaHasta) {
      const hasta = new Date(`${fechaHasta}T00:00:00`);
      if (!Number.isNaN(hasta.getTime())) {
        if (!fechas.length || hasta.getTime() !== fechas[0].getTime()) {
          fechas.push(hasta);
        }
      }
    }
    setRangoFechas(fechas);
  }, [modalAbierto, fechaDesde, fechaHasta]);

  const cargarHistorial = useCallback(async () => {
    setCargandoHistorial(true);
    setErrorHistorial(null);
    try {
      const params = new URLSearchParams({
        page: String(historialPage),
        limit: String(historialLimit),
      });
      const respuesta = await requestJson<{
        ok: boolean;
        registros: ConciliacionHistorialEntry[];
        total: number;
        page: number;
        limit: number;
      }>(`/api/tesoreria/importaciones/monchis/conciliaciones/historial?${params.toString()}`);
      setHistorial(respuesta.registros ?? []);
      setHistorialTotal(respuesta.total ?? 0);
    } catch (err) {
      const mensaje =
        err instanceof Error
          ? err.message
          : "No se pudo cargar el historial de sincronización.";
      setErrorHistorial(mensaje);
    } finally {
      setCargandoHistorial(false);
    }
  }, [historialLimit, historialPage]);

  useEffect(() => {
    void cargarHistorial();
  }, [cargarHistorial]);

  const limpiarModal = () => {
    setModalError(null);
    setVerificacion(null);
    setVerificando(false);
    detenerProgresoSincronizacion();
  };

  const toggleModal = () => {
    setModalAbierto((prev) => {
      const siguiente = !prev;
      if (!siguiente) {
        limpiarModal();
      }
      return siguiente;
    });
  };

  const diasSeleccionados = useMemo(
    () => contarDiasSeleccionados(fechaDesde, fechaHasta),
    [fechaDesde, fechaHasta],
  );
  const superaLimiteDias = diasSeleccionados > 7;

  const payloadBase = useMemo(() => {
    const body: Record<string, string> = {
      fechaDesde,
      fechaHasta,
    };
    if (empresaCodigo.trim()) {
      body.empresaCodigo = empresaCodigo.trim();
    }
    return body;
  }, [fechaDesde, fechaHasta, empresaCodigo]);

  const manejarVerificacion = useCallback(async () => {
    if (!verificarEndpoint || !fechaDesde || !fechaHasta || superaLimiteDias) {
      return;
    }
    setVerificando(true);
    setModalError(null);
    setVerificacion(null);
    try {
      const params = new URLSearchParams(payloadBase);
      const url = appendQueryParams(verificarEndpoint, params);
      const respuesta = await requestJson<VerificacionResponse>(url);
      setVerificacion(respuesta);
    } catch (err) {
      const mensaje =
        err instanceof Error
          ? err.message
          : "No se pudo verificar la disponibilidad del rango seleccionado.";
      setModalError(mensaje);
    } finally {
      setVerificando(false);
    }
  }, [verificarEndpoint, payloadBase, fechaDesde, fechaHasta, superaLimiteDias]);

  useEffect(() => {
    if (!modalAbierto || !fechaDesde || !fechaHasta || superaLimiteDias) {
      return;
    }
    const timer = setTimeout(() => {
      void manejarVerificacion();
    }, 400);
    return () => clearTimeout(timer);
  }, [modalAbierto, fechaDesde, fechaHasta, empresaCodigo, manejarVerificacion, superaLimiteDias]);

  const formatFechaHumana = (valor: string) => {
    const base = new Date(`${valor}T00:00:00`);
    return base.toLocaleDateString("es-PY");
  };
  const bloqueoActivo = Boolean(verificacion?.enProceso);
  const bloqueoDescripcion = verificacion?.enProceso
    ? `${verificacion.enProceso.usuarioNombre ?? "Otro usuario"} está sincronizando del ${formatFechaHumana(
        verificacion.enProceso.fechaDesde,
      )} al ${formatFechaHumana(verificacion.enProceso.fechaHasta)}.`
    : null;
  const puedeSincronizar = useMemo(() => {
    if (!fechaDesde || !fechaHasta) return false;
    return (
      fechaDesde <= fechaHasta &&
      !sincronizando &&
      !bloqueoActivo &&
      !superaLimiteDias
    );
  }, [fechaDesde, fechaHasta, sincronizando, bloqueoActivo, superaLimiteDias]);

  const manejarSincronizacion = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!puedeSincronizar) {
      return;
    }
    setSincronizando(true);
    setModalError(null);
    iniciarProgresoSincronizacion();
    try {
      const respuesta = await requestJson<SincronizacionResponse>(sincronizarEndpoint, {
        method: "POST",
        body: JSON.stringify(payloadBase),
      });
      const baseMensaje = respuesta.message ?? "La sincronización se ejecutó correctamente.";
      const resumenPartes: string[] = [];
      if (typeof respuesta.totalRegistros === "number") {
        resumenPartes.push(`Registros: ${respuesta.totalRegistros}`);
      }
      if (typeof respuesta.conciliacionId === "number") {
        resumenPartes.push(`Conciliación #${respuesta.conciliacionId}`);
      }
      const mensajeFinal =
        resumenPartes.length > 0 ? `${baseMensaje} — Última sincronización ${resumenPartes.join(" · ")}` : baseMensaje;
      setMensaje(mensajeFinal);
      setUltimoResumen({
        diasProcesados: respuesta.diasProcesados ?? [],
        totalRegistros: respuesta.totalRegistros,
        conciliacionId: respuesta.conciliacionId,
      });
      void cargarHistorial();
      setError(null);
      toggleModal();
    } catch (err) {
      const mensaje =
        err instanceof Error
          ? err.message
          : "No se pudo completar la sincronización. Intentá nuevamente.";
      setModalError(mensaje);
      setError(mensaje);
    } finally {
      setSincronizando(false);
      detenerProgresoSincronizacion();
    }
  };

  const empresaSelectOptions = useMemo(() => {
    return empresas
      .map((option) => {
        const value = option.empresaCodigo?.trim() || option.fuente?.trim();
        if (!value) {
          return null;
        }
        const nombreBase = option.empresaNombre?.trim();
        const codigoBase = option.empresaCodigo?.trim();
        const label = nombreBase
          ? codigoBase
            ? `${nombreBase} (${codigoBase})`
            : nombreBase
          : codigoBase || option.fuente || value;
        return { value, label };
      })
      .filter((option): option is { value: string; label: string } => option !== null);
  }, [empresas]);

  const empresaDescripcionSeleccionada = useMemo(() => {
    if (!empresaCodigo) {
      return null;
    }
    const opcion = empresaSelectOptions.find((option) => option.value === empresaCodigo.trim());
    return opcion?.label ?? empresaCodigo;
  }, [empresaCodigo, empresaSelectOptions]);

  const iniciarProgresoSincronizacion = () => {
    detenerProgresoSincronizacion();
    setProgressStage("connecting");
    const timers: NodeJS.Timeout[] = [];
    timers.push(
      setTimeout(() => {
        setProgressStage("downloading");
      }, 1500),
    );
    timers.push(
      setTimeout(() => {
        setProgressStage("saving");
      }, 3000),
    );
    progressTimeoutsRef.current = timers;
  };

  const detenerProgresoSincronizacion = () => {
    progressTimeoutsRef.current.forEach((timer) => clearTimeout(timer));
    progressTimeoutsRef.current = [];
    setProgressStage("idle");
  };

  useEffect(
    () => () => {
      detenerProgresoSincronizacion();
    },
    [],
  );

  const PROGRESS_SEQUENCE: Exclude<ProgresoEtapa, "idle">[] = ["connecting", "downloading", "saving"];
  const progressStageIndex = PROGRESS_SEQUENCE.indexOf(progressStage as Exclude<ProgresoEtapa, "idle">);
  const renderTextoProgreso = (stage: Exclude<ProgresoEtapa, "idle">) => {
    switch (stage) {
      case "connecting":
        return `Conectando a la base de datos ${empresaDescripcionSeleccionada ?? "principal"}…`;
      case "downloading":
        return "Descargando los registros de formas de pago…";
      case "saving":
        return "Guardando los datos para finalizar la sincronización…";
      default:
        return "";
    }
  };

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat("es-PY", { minimumFractionDigits: 0 }),
    [],
  );

  const historialDias = useMemo(() => {
    return historial.flatMap((registro) =>
      (registro.diasDetalle ?? []).map((dia) => ({
        clave: `${registro.id}-${dia.fecha}`,
        fecha: dia.fecha,
        totalRegistros: dia.totalRegistros,
        totalMonto: dia.totalMonto,
        usuario: registro.usuarioNombre || `Usuario #${registro.usuarioId}`,
        empresa: registro.empresaNombre || registro.empresaCodigo,
        ejecutado: registro.creadoEn,
        conciliacionId: registro.id,
      })),
    );
  }, [historial]);

  const eventosCalendario = useMemo(() => {
    return historialDias.map((item) => ({
      title: `${item.empresa} · ${item.totalRegistros ?? 0} regs`,
      start: item.fecha,
      allDay: true,
      display: "block",
      backgroundColor: "#0ab39c",
      borderColor: "#0ab39c",
      textColor: "#fff",
      extendedProps: {
        tipo: "sincronizado",
        empresa: item.empresa,
        totalMonto: item.totalMonto ?? 0,
        totalRegistros: item.totalRegistros ?? 0,
      },
    }));
  }, [historialDias]);

  const totalPaginasHistorial = useMemo(() => {
    return Math.max(Math.ceil(historialTotal / historialLimit), 1);
  }, [historialLimit, historialTotal]);

  const abrirDetalleSucursales = async (conciliacionId: number, fecha: string) => {
    setDetalleConciliacionId(conciliacionId);
    setDetalleFecha(fecha);
    setDetalleModalVisible(true);
    setDetalleLoading(true);
    setDetalleError(null);
    try {
      const params = new URLSearchParams({ fechaDia: fecha });
      const respuesta = await requestJson<{ ok: boolean; registros: ConciliacionDetalleSucursal[] }>(
        `/api/tesoreria/importaciones/monchis/conciliaciones/${conciliacionId}/detalle?${params.toString()}`,
      );
      setDetalleSucursales(respuesta.registros ?? []);
    } catch (err) {
      const mensaje =
        err instanceof Error ? err.message : "No se pudo cargar el detalle por sucursal.";
      setDetalleError(mensaje);
    } finally {
      setDetalleLoading(false);
    }
  };

  return (
    <>
      <Card className="mb-3">
        <CardBody>
          <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
            <div>
              <CardTitle tag="h5" className="mb-1">
                {titulo}
              </CardTitle>
              <p className="text-muted mb-0">{descripcion}</p>
            </div>
            <Button color="primary" onClick={toggleModal}>
              <i className="ri-time-line me-1" />
              Sincronizar
            </Button>
          </div>
          {mensaje && !modalAbierto && (
            <Alert color="success" toggle={() => setMensaje(null)}>
              {mensaje}
            </Alert>
          )}
          {error && !modalAbierto && (
            <Alert color="danger" toggle={() => setError(null)}>
              {error}
            </Alert>
          )}
          <div className="mb-4">
            <CardTitle tag="h6">Calendario de días sincronizados</CardTitle>
            <Alert color="info" className="mt-2">
              Por el volumen de registros solo podés sincronizar hasta 7 días corridos por ejecución.
            </Alert>
            <FullCalendar
              plugins={[dayGridPlugin]}
              initialView="dayGridMonth"
              height={430}
              locales={[esLocale]}
              locale="es"
              events={eventosCalendario}
              eventContent={(arg) => {
                const tipo = arg.event.extendedProps?.tipo;
                const color = tipo === "sin_datos" ? "#f06548" : "#0ab39c";
                const empresa = arg.event.extendedProps?.empresa;
                const totalRegistros = arg.event.extendedProps?.totalRegistros ?? 0;
                const totalMonto = arg.event.extendedProps?.totalMonto ?? 0;
                return (
                  <div
                    style={{
                      backgroundColor: color,
                      color: "#fff",
                      borderRadius: 4,
                      padding: "2px 4px",
                      fontSize: "0.7rem",
                      lineHeight: 1.2,
                    }}
                  >
                    <div className="fw-semibold text-truncate">{empresa}</div>
                    <div>{totalRegistros} regs</div>
                    <div style={{ fontSize: "0.65rem" }}>
                      Gs {numberFormatter.format(totalMonto)}
                    </div>
                  </div>
                );
              }}
              headerToolbar={{ left: "title", center: "", right: "prev,next today" }}
            />
          </div>
          <div className="mb-4">
            <CardTitle tag="h6">Historial de sincronizaciones recientes</CardTitle>
            {errorHistorial ? (
              <Alert color="danger" className="mb-0">
                {errorHistorial}
              </Alert>
            ) : (
              <div className="table-responsive">
                <Table className="align-middle table-nowrap mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Fecha</th>
                      <th>Registros</th>
                      <th>Monto</th>
                      <th>Usuario</th>
                      <th>Empresa</th>
                      <th>Ejecutado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cargandoHistorial ? (
                      <tr>
                        <td colSpan={7} className="text-center py-3">
                          <Spinner size="sm" className="me-2" /> Cargando historial…
                        </td>
                      </tr>
                    ) : historial.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-3 text-muted">
                          Todavía no registramos sincronizaciones.
                        </td>
                      </tr>
                    ) : (
                      historial.map((registro) => {
                        const diasDetalle = registro.diasDetalle ?? [];
                        const totalDiaRegistros = diasDetalle.reduce(
                          (acc, dia) => acc + (dia.totalRegistros ?? 0),
                          0,
                        );
                        const totalDiaMonto = diasDetalle.reduce(
                          (acc, dia) => acc + (dia.totalMonto ?? 0),
                          0,
                        );
                        const descripcionRango =
                          registro.fechaDesde && registro.fechaHasta
                            ? registro.fechaDesde === registro.fechaHasta
                              ? registro.fechaDesde
                              : `${registro.fechaDesde} al ${registro.fechaHasta}`
                            : "Sin rango";
                        const accionContenido =
                          diasDetalle.length === 0 ? (
                            <span className="text-muted small">Sin días</span>
                          ) : diasDetalle.length === 1 ? (
                            <Button
                              color="info"
                              size="sm"
                              onClick={() =>
                                abrirDetalleSucursales(registro.id, diasDetalle[0].fecha)
                              }
                            >
                              Ver {diasDetalle[0].fecha}
                            </Button>
                          ) : (
                            <UncontrolledDropdown>
                              <DropdownToggle color="info" size="sm" caret>
                                Ver por sucursal
                              </DropdownToggle>
                              <DropdownMenu>
                                {diasDetalle.map((dia) => (
                                  <DropdownItem
                                    key={`${registro.id}-${dia.fecha}`}
                                    onClick={() => abrirDetalleSucursales(registro.id, dia.fecha)}
                                  >
                                    {dia.fecha} · {dia.totalRegistros ?? 0} regs
                                  </DropdownItem>
                                ))}
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          );
                        return (
                          <tr key={`hist-${registro.id}`}>
                            <td>
                              <div className="fw-semibold">{descripcionRango}</div>
                              {diasDetalle.length > 1 && (
                                <div className="text-muted small">
                                  {diasDetalle.length} días sincronizados
                                </div>
                              )}
                            </td>
                            <td>{registro.totalRegistros ?? totalDiaRegistros}</td>
                            <td>Gs {numberFormatter.format(totalDiaMonto)}</td>
                            <td>
                              {registro.usuarioNombre ||
                                (registro.usuarioId ? `Usuario #${registro.usuarioId}` : "Desconocido")}
                            </td>
                            <td>{registro.empresaNombre || registro.empresaCodigo}</td>
                            <td>{new Date(registro.creadoEn).toLocaleString("es-PY")}</td>
                            <td>{accionContenido}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <small className="text-muted">
                    Página {historialPage} de {totalPaginasHistorial}
                  </small>
                  <div className="d-flex gap-2">
                    <Button
                      color="light"
                      size="sm"
                      disabled={historialPage <= 1 || cargandoHistorial}
                      onClick={() => setHistorialPage((prev) => Math.max(prev - 1, 1))}
                    >
                      <i className="ri-arrow-left-line" /> Anterior
                    </Button>
                    <Button
                      color="light"
                      size="sm"
                      disabled={historialPage >= totalPaginasHistorial || cargandoHistorial}
                      onClick={() => setHistorialPage((prev) => prev + 1)}
                    >
                      Siguiente <i className="ri-arrow-right-line" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
          {ultimoResumen?.diasProcesados && ultimoResumen.diasProcesados.length > 0 && (
            <Card className="mb-3 bg-light border-0">
              <CardBody>
                <CardTitle tag="h6" className="mb-2">
                  Días procesados en la última sincronización
                </CardTitle>
                <ul className="mb-0 list-unstyled">
                  {ultimoResumen.diasProcesados.map((dia) => (
                    <li key={`procesado-${dia}`}>
                      <i className="ri-check-line text-success me-2" />
                      {dia}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={modalAbierto} toggle={toggleModal} centered size="lg">
        <Form onSubmit={manejarSincronizacion}>
          <ModalHeader toggle={toggleModal}>
            Seleccionar rango a sincronizar
            <p className="text-muted small mb-0 mt-2">
              Podés sincronizar hasta 7 días corridos por ejecución y solo un usuario puede procesar el mismo rango a la vez para cuidar la integridad de los datos.
            </p>
          </ModalHeader>
          <ModalBody>
            <Row className="g-3 align-items-end">
              <Col md={7}>
                <Label className="form-label">Seleccionar rango</Label>
                <Flatpickr
                  className="form-control"
                  options={{
                    mode: "range",
                    dateFormat: "Y-m-d",
                    closeOnSelect: false,
                  }}
                  value={rangoFechas}
                  onChange={(fechas: Date[]) => {
                    setRangoFechas(fechas);
                    if (fechas.length > 0) {
                      const desde = fechas[0].toISOString().slice(0, 10);
                      setFechaDesde(desde);
                    }
                    if (fechas.length > 1) {
                      const hasta = fechas[1].toISOString().slice(0, 10);
                      setFechaHasta(hasta);
                    } else if (fechas.length === 1) {
                      const hasta = fechas[0].toISOString().slice(0, 10);
                      setFechaHasta(hasta);
                    } else {
                      setFechaDesde("");
                      setFechaHasta("");
                    }
                  }}
                />
              </Col>
              <Col md={5}>
                <Label className="form-label">{empresaCodigoLabel}</Label>
                <Input
                  type="select"
                  value={empresaCodigo}
                  onChange={(event) => setEmpresaCodigo(event.target.value)}
                  disabled={cargandoEmpresas || (!empresaSelectOptions.length && !empresaCodigo)}
                >
                  <option value="">
                    {empresaCodigoPlaceholder ?? "Todas las empresas disponibles"}
                  </option>
                  {empresaSelectOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Input>
                {cargandoEmpresas && (
                  <div className="form-text text-muted">Cargando empresas…</div>
                )}
                {errorEmpresas && (
                  <div className="form-text text-warning">{errorEmpresas}</div>
                )}
              </Col>
            </Row>
            {superaLimiteDias && (
              <Alert color="warning" className="mt-3">
                El periodo seleccionado contiene {diasSeleccionados} días. Ajustalo para que no supere los 7 días permitidos por ejecución.
              </Alert>
            )}
            {verificarEndpoint && (
              <Button
                color="link"
                type="button"
                className="px-0 mt-3"
                onClick={manejarVerificacion}
                disabled={
                  verificando ||
                  sincronizando ||
                  !fechaDesde ||
                  !fechaHasta ||
                  superaLimiteDias
                }
              >
                {verificando ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Verificando…
                  </>
                ) : (
                  <>
                    <i className="ri-search-eye-line me-1" />
                    Verificar días disponibles
                  </>
                )}
              </Button>
            )}
            {verificacion && (
              <Alert color={verificacion.diasConDatos?.length ? "warning" : "info"} className="mt-3">
                {verificacion.diasConDatos?.length ? (
                  <>
                    <div className="fw-semibold mb-1">Ya existen registros sincronizados en:</div>
                    <ul className="mb-2">
                      {verificacion.diasConDatos.map((dia) => (
                        <li key={`existe-${dia}`}>{dia}</li>
                      ))}
                    </ul>
                    <p className="mb-2 text-warning">
                      Hay registros que se van a reemplazar. ¿Estás de acuerdo? Igual guardamos el historial.
                    </p>
                  </>
                ) : (
                  <div className="mb-2">No se encontraron registros previos para este rango.</div>
                )}
                {verificacion.diasSinDatos?.length ? (
                  <>
                    <div className="fw-semibold mb-1">Días disponibles para sincronizar:</div>
                    <ul className="mb-0">
                      {verificacion.diasSinDatos.map((dia) => (
                        <li key={`disponible-${dia}`}>{dia}</li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </Alert>
            )}
            {bloqueoActivo && bloqueoDescripcion && (
              <Alert color="warning" className="mt-3">
                {bloqueoDescripcion}
              </Alert>
            )}
            {progressStage !== "idle" && (
              <div className="mt-3">
                <p className="text-muted mb-2">Estado de la sincronización</p>
                <ul className="list-unstyled mb-0">
                  {PROGRESS_SEQUENCE.map((step, index) => {
                    const stepIndex = index;
                    const isCompleted = progressStageIndex > stepIndex;
                    const isActive = progressStage === step;
                    const iconClass = isActive
                      ? "ri-loader-4-line text-info ri-spin"
                      : isCompleted
                        ? "ri-check-line text-success"
                        : "ri-time-line text-muted";
                    return (
                      <li key={step} className="d-flex align-items-center gap-2 mb-1">
                        <i className={`${iconClass} ${isActive ? "spin" : ""}`} />
                        <span className={isCompleted ? "text-success" : isActive ? "text-info" : "text-muted"}>
                          {renderTextoProgreso(step)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {modalError && (
              <Alert color="danger" className="mt-3" toggle={() => setModalError(null)}>
                {modalError}
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="light" type="button" onClick={toggleModal}>
              Cancelar
            </Button>
            <Button color="primary" type="submit" disabled={!puedeSincronizar}>
              {sincronizando ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Conectado al servidor, esperando sincronización…
                </>
              ) : (
                "Sincronizar"
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
      <Modal isOpen={detalleModalVisible} toggle={() => setDetalleModalVisible(false)} size="lg">
        <ModalHeader toggle={() => setDetalleModalVisible(false)}>
          Detalle por sucursal ·{" "}
          {detalleFecha ? new Date(detalleFecha).toLocaleDateString("es-PY") : ""}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted">
            Total facturado incluye facturas a crédito y al contado sincronizadas desde el sistema de gestión.
            {detalleSucursales.length > 0 && (
              <Badge color="primary" pill className="ms-2">
                Total general: Gs{" "}
                {numberFormatter.format(
                  detalleSucursales.reduce((acc, sucursal) => acc + (sucursal.totalMonto ?? 0), 0),
                )}
              </Badge>
            )}
          </p>
          {detalleError && <Alert color="danger">{detalleError}</Alert>}
          {detalleLoading ? (
            <div className="text-center text-muted py-3">
              <Spinner size="sm" className="me-2" /> Cargando detalle…
            </div>
          ) : detalleSucursales.length === 0 ? (
            <div className="text-center text-muted py-3">No encontramos registros para la fecha seleccionada.</div>
          ) : (
            <div className="table-responsive">
              <Table className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Sucursal</th>
                    <th>Registros</th>
                    <th>Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {detalleSucursales.map((sucursal) => (
                    <tr key={`${detalleConciliacionId}-${detalleFecha}-${sucursal.idsucursal ?? 'na'}`}>
                      <td>{sucursal.sucursalNombre}</td>
                      <td>{sucursal.totalRegistros}</td>
                      <td>Gs {numberFormatter.format(sucursal.totalMonto ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setDetalleModalVisible(false)}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default PlataformasSincronizacionPanel;
